import React, { useMemo } from 'react'
import TabBar from '@zambezi/sdk/tab-bar'
import { Select } from '@zambezi/sdk/dropdown-list'
import { useStateContext } from '@zambezi/caballo-vivo'
import { IsViewport } from '@zambezi/layout-grid'
import { findIndex, pipe } from 'ramda'
import { List } from 'immutable'
import classNames from 'classnames'
import { formatDate } from '@apiEnabler/ob-common/lib/date-utils'

import {
  getPaymentDetails,
  createMoneyFormatter,
} from '@apiEnabler/ob-common/lib/view/stores/get-details-from-state'
import PageHeader from './PageHeader'
import PaymentCard, { EmptyPaymentCard } from './PaymentCard'
import ConsentListSectionHeader from './ConsentListSectionHeader'
import NavBar from './NavBar'
import layoutStyle from '../common/api-enabler-styles.module.css'
import style from './Payments.module.css'
import { followStoreLink$ } from '../navigation'

export default function Payments({ type }) {
  const state = useStateContext()
  const serviceName = state.getIn(['brand', 'home'])

  const rawPayments = state.get('payments')

  function transformPayments(type) {
    return state
      .get('payments')
      .get(type, List())
      .toArray()
      .map(getPaymentDetails)
  }

  const tabData = useMemo(
    // these conversions could alternatively be put in the store,
    // to confine knowledge of the backend API there, but they'd probably
    // need to become more agnostic of the view
    () => [
      {
        title: 'Domestic',
        id: 'domestic',
        upcoming: transformPayments('upcomingDomestic'),
        upcomingLabel: 'Upcoming Payments',
        upcomingDescription: 'Payments scheduled for the next 6 months',
        pastLabel: 'Past Payments',
        pastDescription: 'Includes completed and failed payments',
        past: transformPayments('pastDomestic'),
      },
      {
        title: 'International',
        id: 'international',
        upcoming: transformPayments('upcomingInternational'),
        upcomingLabel: 'Upcoming Payments',
        upcomingDescription: 'Payments scheduled for the next 6 months',
        pastLabel: 'Past Payments',
        pastDescription: 'Includes completed and failed payments',
        past: transformPayments('pastInternational'),
      },
      {
        title: 'Bulk',
        id: 'bulk',
        upcoming: transformPayments('upcomingBulk'),
        upcomingLabel: 'Upcoming Bulk Payments',
        upcomingDescription: `You’ll find more information on the status of individual payments in ${serviceName} or with your service provider.`,
        pastLabel: 'Past Bulk Payments',
        pastDescription: '',
        past: transformPayments('pastBulk'),
      },
      {
        title: 'Transfers',
        id: 'transfer',
        pastLabel: 'Balance transfers',
        pastDescription: 'Includes completed and failed balance transfers',
        past: transformPayments('pastBalanceTransfer'),
      },
    ],
    [rawPayments]
  )

  const selectedTabIndex = pipe(
    findIndex(tab => tab.id === type),
    index => (index === -1 ? 0 : index)
  )(tabData)

  const selectedTab = tabData[selectedTabIndex]

  return (
    <section className="js-payments">
      <PageHeader
        navbar={<NavBar />}
        title="Payments and transfers"
        subtitle="Payments that you have set up via service providers."
      />
      <div className={layoutStyle.overviewSections}>
        <IsViewport isWidescreen isDesktop>
          <TabBar
            className={classNames(style.tabs, 'js-payment-type-tabs')}
            data={tabData}
            selectedIndex={selectedTabIndex}
            onSelected={({ id }) => {
              followStoreLink$.next(`/payments?type=${id}`)
            }}
            renderFn={({ id, title }) => (
              <span
                className={classNames(style.tab, `js-payment-type-tab-${id}`)}
              >
                {title}
              </span>
            )}
          />

          {selectedTab.upcoming && (
            <PaymentSection
              label={selectedTab.upcomingLabel}
              description={selectedTab.upcomingDescription}
              payments={selectedTab.upcoming}
              className="js-upcoming-payments"
              pointedBorder
            />
          )}
        </IsViewport>

        <IsViewport isTablet isMobile>
          <div className={style.paymentType}>
            <span>Select payment type:</span>
            <Select
              aria-label="Please select payment type"
              id="paymentsType"
              defaultValue={tabData[selectedTabIndex].title}
              suggestions={tabData.map(paymentType => paymentType.title)}
              className={classNames(
                style.dropdownField,
                'js-payments-type-dropdown'
              )}
              onChange={(event, { value }) => {
                const selectedPaymentType = tabData.filter(
                  el => el.title === value
                )[0]
                followStoreLink$.next(
                  `/payments?type=${selectedPaymentType.id}`
                )
              }}
            />
          </div>

          {selectedTab.upcoming && (
            <PaymentSection
              label={selectedTab.upcomingLabel}
              description={selectedTab.upcomingDescription}
              payments={selectedTab.upcoming}
              className="js-upcoming-payments"
            />
          )}
        </IsViewport>

        <PaymentSection
          label={selectedTab.pastLabel}
          description={selectedTab.pastDescription}
          payments={selectedTab.past}
          className="js-past-payments"
          pointedBorder={!selectedTab.upcoming}
        />
      </div>
    </section>
  )
}

function PaymentSection({
  label,
  description,
  payments,
  className,
  pointedBorder,
}) {
  const state = useStateContext()

  const brand = state.get('brand')
  const paymentDetails = payments.map(payment =>
    getPaymentCardViewModel(payment, brand)
  )
  return (
    <section className={classNames(layoutStyle.overviewSection, className)}>
      <ConsentListSectionHeader
        count={payments.length}
        label={label}
        description={description}
        consentType="payment"
        indented
        pointedBorder={pointedBorder}
      />
      {paymentDetails.length > 0 ? (
        paymentDetails.map((payment, index) => {
          return (
            <PaymentCard
              key={payment.id}
              {...payment}
              className={`js-payment-card-${index}`}
            />
          )
        })
      ) : (
        <EmptyPaymentCard label={label} />
      )}
    </section>
  )
}

function getPaymentCardViewModel(payment, brand) {
  const moneyFormatter = createMoneyFormatter(payment)
  return {
    id: payment.id,
    description: payment.isBulk
      ? `Ref: ${payment.fileReference}`
      : payment.recipientName,
    avatarText: payment.isBulk
      ? payment.individualPayments.length
      : payment.recipientName,
    detailUrl: `/payments/${payment.id}`,
    amountString: moneyFormatter(payment.debitAmount),
    amountAnnotation:
      payment.isInternational &&
      payment.debitAmount.currency !== payment.creditAmount.currency &&
      moneyFormatter(payment.creditAmount),
    dateText: createDateText(payment, brand),
    status: payment.status,
    showStatusIcon: !payment.isBulk,
  }
}

function getPaymentTypeLabel(paymentType, brand) {
  switch (paymentType) {
    case 'SINGLE_IMMEDIATE':
      return brand.get('paymentLabelSingleImmediate')
    case 'DOMESTIC_SCHEDULED':
      return brand.get('paymentLabelScheduled')
    case 'FILE_DOMESTIC_SCHEDULED':
    case 'FILE_SINGLE_IMMEDIATE':
      return 'Bulk file'
    case 'BALANCE_TRANSFER':
      return 'Balance transfer'
    default:
      return 'Payment'
  }
}

function getActivityLabel({ isBulk, status }) {
  const activityLabels = {
    FAILED: isBulk ? 'submission failed' : 'failed',
    SUCCEEDED: isBulk ? 'submitted' : 'made',
    CANCELLED: 'cancelled',
    DECLINED: 'declined',
    AUTHORIZED: 'confirmed',
    CONSUMED: 'confirmed',
    SUBMITTING: 'initiated',
    SUBMITTED: 'in progress',
    REJECTED: 'rejected',
  }

  return activityLabels[status]
}

function formatDestinationCountry(payment) {
  if (payment.isInternational) {
    return payment.destinationCountryName
      ? `to ${payment.destinationCountryName}`
      : ''
  }
  return ''
}

function createDateText(payment, brand) {
  return payment.isUpcoming
    ? formatUpcomingPaymentInfo(payment)
    : formatPastPaymentInfo(payment, brand)
}

function formatUpcomingPaymentInfo(payment) {
  return `Payment ${formatDestinationCountry(payment)} due on ${formatDate(
    payment.upcomingPaymentDate
  )}`
}

function formatPastPaymentInfo(payment, brand) {
  const paymentType = getPaymentTypeLabel(payment.type, brand)
  const paymentActivity = getActivityLabel(payment)
  const btPaymentInfo = `${paymentType} ${formatDestinationCountry(
    payment
  )} ${paymentActivity}`

  return payment.isBalanceTransfer
    ? btPaymentInfo
    : `${paymentType} ${formatDestinationCountry(
        payment
      )} ${paymentActivity} on ${formatDate(payment.pastPaymentDate)}`
}
